import {Box, Button, Checkbox, Divider, Stack, Typography, useTheme} from '@mui/material';
import React from 'react';
import {useNavigate} from 'react-router-dom';

import {StyledCard, TagChip} from 'presentation/components';
import {translations as newTranslations, translations} from 'presentation/translations-new';
import {formatDateString, formatPriceBill, formatText} from 'presentation/utils';

import {BillStatusEnum, MyPaymentsEnum} from 'presentation/constants';
import {FinancialCardV2Props, StatusConfigProps} from './types';

const {interestFinesV2, billStatus, dueDate, reference} =
	translations['pt-br'].privateFlow.myFinancial;
const {showDetails} = newTranslations['pt-br'].commons;

export function FinancialCardV2({
	bill,
	onCheckOlderBillsOnDetails,
	hideInterestFines = false,
	hideBorder = false,
	dueDateLabel,
	referenceFirst = false,
	onViewDetails,
	selected,
	onSelect,
	checkboxDisabled,
	checkboxId,
}: FinancialCardV2Props) {
	const navigate = useNavigate();
	const {palette, colors} = useTheme();

	const {dtPrevista, status: rawStatus, linhaDigitavel} = bill;

	if (!linhaDigitavel) {
		return null;
	}

	const {vlObrigacao, dtVencimento} = linhaDigitavel;

	const status =
		rawStatus === BillStatusEnum.OPEN || rawStatus === BillStatusEnum.PAID
			? rawStatus
			: BillStatusEnum.EXPIRED;

	const formattedTargetDate = formatDateString(dtPrevista, 'dd/MM/yyyy', 'MMM/yyyy');
	const formattedPrice = vlObrigacao ? formatPriceBill(vlObrigacao).split('\u00A0') : ['R$', '-'];

	const statusConfig: Partial<Record<BillStatusEnum, StatusConfigProps>> = {
		[BillStatusEnum.OPEN]: {
			chipType: 'info',
			color: colors.light.info['30Percent'],
			chipText: billStatus.open,
		},
		[BillStatusEnum.EXPIRED]: {
			chipType: 'error',
			color: colors.light.error['30Percent'],
			chipText: billStatus.expired,
		},
		[BillStatusEnum.PAID]: {
			chipType: 'success',
			color: colors.light.success['30Percent'],
			chipText: billStatus.paid,
		},
	};

	const {chipType, color, chipText} =
		statusConfig[status] ?? statusConfig[BillStatusEnum.EXPIRED]!;

	const handleGoToDetails = () => {
		if (onCheckOlderBillsOnDetails) {
			const hasOlderBills = onCheckOlderBillsOnDetails();
			if (hasOlderBills) return;
		}
		navigate(MyPaymentsEnum.OPEN_BILLS, {state: {preselectedBill: bill}});
	};

	const shouldShowFeeAlert = !hideInterestFines && status !== BillStatusEnum.OPEN;

	return (
		<Stack gap={1}>
			<StyledCard.Container
				$borderTop={!hideBorder}
				$borderColor={hideBorder ? undefined : color}
				elevation={0}
				$customPadding="16px 16px 8px"
				$customGap="8px">
				<Stack direction="row" alignItems="stretch" gap={1}>
					{onSelect || checkboxDisabled ? (
						<Box display="flex" alignItems="flex-start" sx={{pt: '6px'}}>
							<span id={checkboxId}>
								<Checkbox
									checked={selected ?? false}
									onChange={onSelect}
									disabled={checkboxDisabled}
									size="small"
									sx={{p: 0}}
								/>
							</span>
						</Box>
					) : null}
					<Stack flex={1}>
						<Stack justifyContent="space-between" alignItems="center" direction="row">
							<Stack direction="row">
								<Typography
									color={palette.text.primary}
									fontWeight={600}
									variant="h6">
									{formattedPrice[0]} {formattedPrice[1]}
								</Typography>
								{status === BillStatusEnum.EXPIRED ? (
									<Typography
										color={palette.text.error}
										fontWeight={600}
										variant="h6">
										*
									</Typography>
								) : null}
							</Stack>
							<TagChip label={chipText} color={chipType} />
						</Stack>
						<Stack gap={0.25}>
							{referenceFirst ? (
								<>
									<Stack direction="row" alignItems="center" gap={0.25}>
										<Typography variant="body2" color={palette.text.secondary}>
											{reference}
										</Typography>
										<Typography color={palette.text.primary}>
											{formatText(formattedTargetDate)}
										</Typography>
									</Stack>
									<Stack direction="row" alignItems="center" gap={0.25}>
										<Typography variant="body2" color={palette.text.secondary}>
											{dueDateLabel ?? dueDate}
										</Typography>
										<Typography color={palette.text.primary}>
											{dtVencimento || '-'}
										</Typography>
									</Stack>
								</>
							) : (
								<>
									<Stack direction="row" alignItems="center" gap={0.25}>
										<Typography variant="body2" color={palette.text.secondary}>
											{dueDateLabel ?? dueDate}
										</Typography>
										<Typography color={palette.text.primary}>
											{dtVencimento || '-'}
										</Typography>
									</Stack>
									<Stack direction="row" alignItems="center" gap={0.25}>
										<Typography variant="body2" color={palette.text.secondary}>
											{reference}
										</Typography>
										<Typography color={palette.text.primary}>
											{formatText(formattedTargetDate)}
										</Typography>
									</Stack>
								</>
							)}
						</Stack>
					</Stack>
				</Stack>
				<Divider sx={{margin: '0 -16px'}} />
				<Stack alignItems={'end'}>
					<Button
						size="small"
						variant="outlined"
						onClick={onViewDetails ?? handleGoToDetails}>
						{showDetails}
					</Button>
				</Stack>
			</StyledCard.Container>
			{shouldShowFeeAlert ? (
				<Stack>
					<Typography variant="caption" color={palette.text.secondary}>
						{interestFinesV2}
					</Typography>
				</Stack>
			) : null}
		</Stack>
	);
}
